import React from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import {
    ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip as RTooltip, Legend,
    CartesianGrid,
} from 'recharts';
import {
    fmtPct, fmtDelta, rateClass, RATE_COLORS, MIN_RUNS, MIN_NIGHTS, TOP_N,
    SERIES_PALETTE, ROLLING,
} from './format';
import { rolling } from './aggregation';

// Tiny axis-less sparkline of an entity's rolling run rate. Visual trajectory
// only — exact values exposed via the card tooltip.
const Sparkline = ({ values, color }) => {
    const data = values.map((v, i) => ({ i, v }));
    return (
        <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 2, right: 2, left: 2, bottom: 2 }}>
                <YAxis hide domain={[0, 100]} />
                <XAxis hide dataKey="i" />
                <Line type="monotone" dataKey="v" stroke={color} strokeWidth={1.6}
                    dot={false} isAnimationActive={false} connectNulls={false} />
            </LineChart>
        </ResponsiveContainer>
    );
};

// One card per ranked entity. Two flavors:
//   mode='rate'  → headline = rate, color by rateClass
//   mode='delta' → headline = Δ clean-night, color by sign
const RankCard = ({ row, kind, data, perEntity, mode }) => {
    const isClient = kind === 'client';
    const district = isClient ? data.districts[row.idx] : null;
    const sparkValues = rolling(perEntity[row.idx].run, ROLLING);
    const positiveDelta = (row.delta_clean || 0) >= 0;

    const headline = mode === 'delta' ? row.delta_clean : row.last14_clean;
    const headlineText = mode === 'delta' ? fmtDelta(row.delta_clean).text : fmtPct(headline);
    const color = mode === 'delta'
        ? (positiveDelta ? RATE_COLORS.good : RATE_COLORS.bad)
        : RATE_COLORS[rateClass(row.last14_clean)];
    const subDelta = fmtDelta(row.delta_clean);
    const studentsLabel = isClient
        ? `${(data.students[row.idx] || 0).toLocaleString()} students`
        : null;

    const tip = (
        <Box sx={{ p: 0.5, fontSize: 11, lineHeight: 1.6 }}>
            <Box sx={{ fontWeight: 700 }}>{row.name}</Box>
            {district && <Box sx={{ color: '#bbb' }}>{district}</Box>}
            <Box>L14d: clean <b>{fmtPct(row.last14_clean)}</b> · run {fmtPct(row.last14_run)}</Box>
            <Box>P14d: clean {fmtPct(row.prior14_clean)} · run {fmtPct(row.prior14_run)}</Box>
            <Box>Full: clean {fmtPct(row.full_clean)} · run {fmtPct(row.full_run)}</Box>
            <Box>{row.worst_run >= 100 ? 'Never dipped' : `Worst day: ${row.worst_run.toFixed(1)}% on ${data.dates[row.worst_run_idx]}`}</Box>
            <Box>{row.volume.toLocaleString()} runs across {row.nightsRan} nights</Box>
        </Box>
    );

    return (
        <Tooltip title={tip} arrow>
            <Box sx={{
                border: '1px solid var(--color-border, #e0e0e0)',
                borderTop: `3px solid ${color}`,
                borderRadius: 1.5,
                p: 1.25,
                background: 'var(--color-bg-paper, #fff)',
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
                cursor: 'help',
                minWidth: 0,
                transition: 'box-shadow 0.15s',
                '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.08)' },
            }}>
                <Box sx={{ overflow: 'hidden' }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {row.name}
                        {isClient && data.unsure[row.idx] && <span className="sh-unsure" title="District identity uncertain"> *</span>}
                    </Typography>
                    {studentsLabel && (
                        <Typography sx={{ fontSize: 10, color: '#888' }}>{studentsLabel}</Typography>
                    )}
                </Box>
                <Box sx={{ height: 36, mx: -0.5 }}>
                    <Sparkline values={sparkValues} color={color} />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 0.5 }}>
                    <Typography sx={{ fontSize: 20, fontWeight: 700, color, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
                        {headlineText}
                    </Typography>
                    {mode === 'rate' && (
                        <Typography className={`sh-${subDelta.cls}`} sx={{ fontSize: 11, fontVariantNumeric: 'tabular-nums' }}>
                            {subDelta.text}
                        </Typography>
                    )}
                </Box>
                <Typography sx={{ fontSize: 10, color: '#888' }}>
                    {row.volume.toLocaleString()} runs · {row.nightsRan} nights
                </Typography>
            </Box>
        </Tooltip>
    );
};

const RankingGrid = ({ rows, kind, data, perEntity, mode }) => {
    if (!rows.length) {
        return <Typography sx={{ fontSize: 12, color: '#888', mt: 0.5 }}>(no qualifying entities)</Typography>;
    }
    return (
        <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(5, 1fr)' },
            gap: 1.25,
        }}>
            {rows.map((r) => (
                <RankCard key={r.idx} row={r} kind={kind} data={data} perEntity={perEntity} mode={mode} />
            ))}
        </Box>
    );
};

const SmallMultiples = ({ rows, perEntity, data, label }) => {
    if (!rows.length) return null;
    const top = rows.slice(0, TOP_N);
    const chartData = data.dates.map((d, i) => {
        const point = { date: d.slice(5) };
        top.forEach((r, si) => {
            point[`s${si}`] = rolling(perEntity[r.idx].run, ROLLING)[i];
        });
        return point;
    });
    return (
        <Box sx={{ mt: 1.5 }}>
            <Typography sx={{ fontSize: 12, color: '#888', mb: 0.5 }}>{label} — 7d rolling run rate.</Typography>
            <Box sx={{ width: '100%', height: 280 }}>
                <ResponsiveContainer>
                    <LineChart data={chartData} margin={{ top: 12, right: 16, left: 0, bottom: 8 }}>
                        <CartesianGrid stroke="#eee" vertical={false} />
                        <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#666' }}
                            interval={Math.max(0, Math.floor(chartData.length / 8))} />
                        <YAxis domain={[-2, 102]} ticks={[0, 20, 40, 60, 80, 100]}
                            tick={{ fontSize: 11, fill: '#666' }}
                            label={{ value: 'Run rate (%)', angle: -90, position: 'insideLeft', style: { fontSize: 12, fill: '#444' } }} />
                        <RTooltip
                            formatter={(v) => (v == null ? '—' : `${v.toFixed(2)}%`)}
                            contentStyle={{ fontSize: 12 }}
                        />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                        {top.map((r, si) => (
                            <Line key={r.idx} type="monotone" dataKey={`s${si}`} name={r.name}
                                stroke={SERIES_PALETTE[si % SERIES_PALETTE.length]} strokeWidth={2}
                                dot={false} connectNulls={false} />
                        ))}
                    </LineChart>
                </ResponsiveContainer>
            </Box>
        </Box>
    );
};

const Block = ({ title, children }) => (
    <Box sx={{ mt: 2 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'var(--color-text-primary)', mb: 0.75 }}>
            {title}
        </Typography>
        {children}
    </Box>
);

const RankingsSection = ({ data, agg }) => {
    const {
        worstClients, bestClients, regressorClients, improverClients,
        worstProviders, bestProviders, regressorProviders, improverProviders,
        clientsRankedCount, clientsTotalCount, providersRankedCount, providersTotalCount,
        perClient, perProvider,
    } = agg;

    const filterNote = `Filter: ≥ ${MIN_RUNS} runs and ≥ ${MIN_NIGHTS} nights over the span. Hover any card for full detail.`;

    const Section = ({ title, children, count }) => (
        <Box className="sh-card">
            <Box sx={{ mb: 1.5 }}>
                <Typography className="sync-health-section-title">{title}</Typography>
                <Typography sx={{ fontSize: 12, color: '#888', mt: 0.5 }}>{filterNote} {count}</Typography>
            </Box>
            {children}
        </Box>
    );

    return (
        <>
            <Section title="Clients" count={`${clientsRankedCount} of ${clientsTotalCount} clients qualify.`}>
                <Block title="Worst 5 — last 14d clean-night rate">
                    <RankingGrid rows={worstClients} kind="client" data={data} perEntity={perClient} mode="rate" />
                    <SmallMultiples rows={worstClients} perEntity={perClient} data={data} label="Worst-5 clients" />
                </Block>
                <Block title="Best 5 — last 14d clean-night rate">
                    <RankingGrid rows={bestClients} kind="client" data={data} perEntity={perClient} mode="rate" />
                </Block>
                <Block title="Biggest regressors — Δ clean-night (L14d − P14d)">
                    <RankingGrid rows={regressorClients} kind="client" data={data} perEntity={perClient} mode="delta" />
                </Block>
                <Block title="Biggest improvers — Δ clean-night (L14d − P14d)">
                    <RankingGrid rows={improverClients} kind="client" data={data} perEntity={perClient} mode="delta" />
                </Block>
            </Section>

            <Section title="Providers" count={`${providersRankedCount} of ${providersTotalCount} providers qualify.`}>
                <Block title="Worst 5 — last 14d clean-night rate">
                    <RankingGrid rows={worstProviders} kind="provider" data={data} perEntity={perProvider} mode="rate" />
                    <SmallMultiples rows={worstProviders} perEntity={perProvider} data={data} label="Worst-5 providers" />
                </Block>
                <Block title="Best 5 — last 14d clean-night rate">
                    <RankingGrid rows={bestProviders} kind="provider" data={data} perEntity={perProvider} mode="rate" />
                </Block>
                <Block title="Biggest regressors — Δ clean-night">
                    <RankingGrid rows={regressorProviders} kind="provider" data={data} perEntity={perProvider} mode="delta" />
                </Block>
                <Block title="Biggest improvers — Δ clean-night">
                    <RankingGrid rows={improverProviders} kind="provider" data={data} perEntity={perProvider} mode="delta" />
                </Block>
            </Section>
        </>
    );
};

export default RankingsSection;
